// src/components/atomic-crm/providers/commons/activity.ts
import type { DataProvider, Identifier } from "ra-core";

import {
  COMPANY_CREATED,
  COMPANY_NOTE_CREATED,
  CONTACT_CREATED,
  CONTACT_NOTE_CREATED,
  DEAL_CREATED,
  DEAL_NOTE_CREATED,
} from "../../consts";
import type {
  Activity,
  Company,
  CompanyNote,
  Contact,
  ContactNote,
  Deal,
  DealNote,
} from "../../types";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";

// ---------------------------------------------------------------------------
// Activity log
//
// Two strategies:
//
//   • Deal scope  → single RPC call (`activity_for_deal`) that joins the deal,
//                   its notes, its contacts, those contacts' notes, its
//                   company, and the company's notes in one round trip.
//
//   • Company scope → single query against `crm_activities`, filtered by
//                   `company_id` (denormalized on write). No fan-out needed
//                   because every user-authored activity is stamped with a
//                   company_id regardless of where it was created.
//
//   • Sales scope  → client-side fan-out across companies / contacts / deals
//                   owned by that sales rep, then a single query against
//                   `crm_activities` for the union.
//
// Note: this file returns "activity log" entries for the **Feed** tab. The
// user-authored notes and tasks live in `crm_activities` and are surfaced
// through `ActivityStream`. The Feed combines ingestion events with a
// select set of high-signal events (creation of companies/contacts/deals,
// plus recent notes).
// ---------------------------------------------------------------------------

export async function getActivityLog(
  dataProvider: DataProvider,
  companyId?: Identifier,
  salesId?: Identifier,
  dealId?: Identifier,
): Promise<Activity[]> {
  // ---------------------------------------------------------------------
  // Deal-scoped view — one RPC, one round trip
  // ---------------------------------------------------------------------
  if (dealId) {
    const { data, error } = await supabase.rpc("activity_for_deal", {
      p_deal_id: dealId,
    });
    if (error) throw error;

    return (data ?? []).map((row: any): Activity => {
      const base = {
        id: row.id as string,
        date: row.date as string,
        company_id: row.company_id ?? undefined,
        sales_id: row.sales_id ?? undefined,
      };

      switch (row.type) {
        case COMPANY_CREATED:
          return {
            ...base,
            type: COMPANY_CREATED,
            company: row.payload.company as Company,
          };
        case CONTACT_CREATED:
          return {
            ...base,
            type: CONTACT_CREATED,
            contact: row.payload.contact as Contact,
          };
        case CONTACT_NOTE_CREATED:
          return {
            ...base,
            type: CONTACT_NOTE_CREATED,
            contactNote: row.payload.contactNote as ContactNote,
          };
        case DEAL_CREATED:
          return {
            ...base,
            type: DEAL_CREATED,
            deal: row.payload.deal as Deal,
          };
        case DEAL_NOTE_CREATED:
          return {
            ...base,
            type: DEAL_NOTE_CREATED,
            dealNote: row.payload.dealNote as DealNote,
          };
        case COMPANY_NOTE_CREATED:
          return {
            ...base,
            type: COMPANY_NOTE_CREATED,
            companyNote: row.payload.companyNote as CompanyNote,
          };
        default:
          throw new Error(
            `activity_for_deal: unknown activity type "${row.type}"`,
          );
      }
    });
  }

  // ---------------------------------------------------------------------
  // Company scope — one query against crm_activities
  //
  // The denormalization trigger stamps every activity with a company_id
  // (inherited from the contact or deal when the client didn't set one),
  // so this is a flat filter — no join needed.
  // ---------------------------------------------------------------------
  if (companyId) {
    const { data, error } = await supabase
      .from("crm_activities")
      .select("*")
      .eq("company_id", companyId)
      .order("date", { ascending: false })
      .limit(50);

    if (error) throw error;

    return (data ?? []).map(rowToActivity);
  }

  // ---------------------------------------------------------------------
  // Sales scope — fan-out over owned records, then a single crm_activities
  // query for the union of created companies/contacts/deals and their notes.
  // ---------------------------------------------------------------------
  if (salesId) {
    // 1. Companies, contacts, deals owned by this sales rep
    const [{ data: companies }, { data: contacts }, { data: deals }] =
      await Promise.all([
        dataProvider.getList<Company>("companies", {
          filter: { "sales_id@in": `(${salesId})` },
          pagination: { page: 1, perPage: 50 },
          sort: { field: "created_at", order: "DESC" },
        }),
        dataProvider.getList<Contact>("contacts", {
          filter: { "sales_id@in": `(${salesId})` },
          pagination: { page: 1, perPage: 50 },
          sort: { field: "first_seen", order: "DESC" },
        }),
        dataProvider.getList<Deal>("deals", {
          filter: { "sales_id@in": `(${salesId})` },
          pagination: { page: 1, perPage: 50 },
          sort: { field: "created_at", order: "DESC" },
        }),
      ]);

    // 2. Recent notes and tasks on those records
    const companyIds = companies.map((c) => c.id);
    const contactIds = contacts.map((c) => c.id);
    const dealIds = deals.map((d) => d.id);

    const orClauses: string[] = [];
    if (companyIds.length)
      orClauses.push(`company_id.in.(${companyIds.join(",")})`);
    if (contactIds.length)
      orClauses.push(`contact_id.in.(${contactIds.join(",")})`);
    if (dealIds.length) orClauses.push(`deal_id.in.(${dealIds.join(",")})`);

    let notes: any[] = [];
    if (orClauses.length) {
      const { data, error } = await supabase
        .from("crm_activities")
        .select("*")
        .or(orClauses.join(","))
        .order("date", { ascending: false })
        .limit(50);
      if (error) throw error;
      notes = data ?? [];
    }

    // 3. Merge creation events with notes, sort, cap
    const creationEvents: Activity[] = [
      ...companies.map((company) => ({
        id: `company.${company.id}.created`,
        type: COMPANY_CREATED,
        company_id: company.id,
        company,
        sales_id: company.sales_id,
        date: company.created_at,
      })),
      ...contacts.map((contact) => ({
        id: `contact.${contact.id}.created`,
        type: CONTACT_CREATED,
        company_id: contact.company_id,
        sales_id: contact.sales_id,
        contact,
        date: contact.first_seen,
      })),
      ...deals.map((deal) => ({
        id: `deal.${deal.id}.created`,
        type: DEAL_CREATED,
        company_id: deal.company_id,
        sales_id: deal.sales_id,
        deal,
        date: deal.created_at,
      })),
    ];

    return [...creationEvents, ...notes.map(rowToActivity)]
      .sort((a, b) =>
        a.date && b.date ? a.date.localeCompare(b.date) * -1 : 0,
      )
      .slice(0, 50);
  }

  // ---------------------------------------------------------------------
  // No scope — return the most recent crm_activities as a fallback
  // ---------------------------------------------------------------------
  const { data, error } = await supabase
    .from("crm_activities")
    .select("*")
    .order("date", { ascending: false })
    .limit(50);

  if (error) throw error;
  return (data ?? []).map(rowToActivity);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Map a raw `crm_activities` row into an `Activity` shape the feed can render.
 * Notes and tasks both come back through this — the renderer uses `type` to
 * decide whether to render a note card or a task card.
 */
function rowToActivity(row: any): Activity {
  const base = {
    id: `crmActivity.${row.id}`,
    date: row.date,
    company_id: row.company_id ?? undefined,
    sales_id: row.sales_id ?? undefined,
  };

  if (row.type === "task") {
    // Reuse the DEAL_NOTE_CREATED variant as a generic "activity" bucket,
    // or add a proper ACTIVITY_CREATED const if you want to distinguish.
    // For now, treat it like a note for feed display purposes.
    return {
      ...base,
      type: CONTACT_NOTE_CREATED,
      contactNote: {
        id: row.id,
        contact_id: row.contact_id,
        text: row.text,
        date: row.date,
        sales_id: row.sales_id,
        status: row.status,
      },
    } as Activity;
  }

  return {
    ...base,
    type: CONTACT_NOTE_CREATED,
    contactNote: {
      id: row.id,
      contact_id: row.contact_id,
      text: row.text,
      date: row.date,
      sales_id: row.sales_id,
      status: row.status,
    },
  } as Activity;
}
